var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import client from 'api/environment';
import UPDATE_USER_AVATAR from 'api/mutations/UPDATE_USER_AVATAR';
import GET_CURRENT_TENANT from 'api/queries/GET_CURRENT_TENANT';
import GET_ORGANIZATIONS from 'api/queries/GET_ORGANIZATIONS';
import PROFILE from 'api/queries/PROFILE';
import { setActiveOrganizationId } from 'utils/activeOrganization';
const initialState = {
    data: null,
    tennant: null,
    organizations: [],
    organizationRole: null,
    status: {
        fetch: 'idle',
        fetchTennant: 'idle',
    },
    error: {
        fetch: null,
        fetchTennant: null,
    },
    refetchFlags: {
        fetch: false,
        fetchTennant: false,
    },
};
export const fetchUserProfile = createAsyncThunk('user/fetchUserProfile', (_1, _a) => __awaiter(void 0, [_1, _a], void 0, function* (_, { rejectWithValue }) {
    try {
        const response = yield client.query({
            query: PROFILE,
            fetchPolicy: 'network-only',
        });
        return Object.assign(Object.assign({}, response.data.me), { readOnly: response.data.readOnly });
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const fetchCurrentTennant = createAsyncThunk('user/fetchCurrentTennant', (_1, _a) => __awaiter(void 0, [_1, _a], void 0, function* (_, { rejectWithValue }) {
    try {
        const response = yield client.query({
            query: GET_CURRENT_TENANT,
            fetchPolicy: 'network-only',
        });
        return response.data.me.currentTenant;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const fetchOrganizations = createAsyncThunk('user/fetchOrganizations', (_1, _a) => __awaiter(void 0, [_1, _a], void 0, function* (_, { rejectWithValue }) {
    var _b, _c, _d, _e, _f;
    try {
        const response = yield client.query({
            query: GET_ORGANIZATIONS,
            fetchPolicy: 'network-only',
        });
        const { me } = response.data;
        return {
            organizations: ((_c = (_b = me === null || me === void 0 ? void 0 : me.tenants) === null || _b === void 0 ? void 0 : _b.edges) !== null && _c !== void 0 ? _c : [])
                .map((edge) => edge === null || edge === void 0 ? void 0 : edge.node)
                .filter(Boolean)
                .map((node) => {
                var _a;
                return ({
                    id: node.id,
                    name: node.name,
                    provisioningStatus: (_a = node.provisioningStatus) !== null && _a !== void 0 ? _a : null,
                });
            }),
            organizationRole: (_d = me === null || me === void 0 ? void 0 : me.organizationRole) !== null && _d !== void 0 ? _d : null,
            currentOrganizationId: (_f = (_e = me === null || me === void 0 ? void 0 : me.currentTenant) === null || _e === void 0 ? void 0 : _e.id) !== null && _f !== void 0 ? _f : null,
        };
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const updateUserAvatar = createAsyncThunk('user/updateUserAvatar', (_a, _b) => __awaiter(void 0, [_a, _b], void 0, function* ({ avatar, userId }, { rejectWithValue }) {
    try {
        const response = yield client.mutate({
            mutation: UPDATE_USER_AVATAR,
            variables: {
                avatar,
                userId,
            },
        });
        return response.data.updateUser.user;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        logout: (state) => {
            state.data = null;
            state.tennant = null;
            state.organizations = [];
            state.organizationRole = null;
            setActiveOrganizationId(null);
        },
        updateUserAvatarStore: (state, action) => {
            state.data = Object.assign(Object.assign({}, state.data), { avatar: action.payload });
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchUserProfile.pending, (state) => {
            state.status.fetch = 'loading';
            state.refetchFlags.fetch = false;
        })
            .addCase(fetchUserProfile.fulfilled, (state, action) => {
            state.data = {
                id: action.payload.id,
                email: action.payload.email,
                username: action.payload.username,
                avatar: action.payload.avatar,
                maintenance: action.payload.maintenance,
                readOnly: action.payload.readOnly,
            };
            state.status.fetch = 'succeeded';
        })
            .addCase(fetchUserProfile.rejected, (state, action) => {
            state.status.fetch = 'failed';
            state.error.fetch = action.error.message || 'Error fetching components';
        })
            .addCase(fetchCurrentTennant.pending, (state) => {
            state.status.fetchTennant = 'loading';
            state.refetchFlags.fetchTennant = false;
        })
            .addCase(fetchCurrentTennant.fulfilled, (state, action) => {
            state.tennant = action.payload;
            state.status.fetchTennant = 'succeeded';
        })
            .addCase(fetchCurrentTennant.rejected, (state, action) => {
            state.status.fetchTennant = 'failed';
            state.error.fetchTennant = action.error.message || 'Error fetching components';
        })
            .addCase(updateUserAvatar.pending, (state) => {
            state.status.update = 'loading';
            state.refetchFlags.fetch = false;
        })
            .addCase(fetchOrganizations.fulfilled, (state, action) => {
            state.organizations = action.payload.organizations;
            state.organizationRole = action.payload.organizationRole;
            // Record what the backend actually resolved, so the header and the server agree even
            // if nothing was stored yet or the stored value was stale.
            if (action.payload.currentOrganizationId) {
                setActiveOrganizationId(action.payload.currentOrganizationId);
            }
        })
            .addCase(fetchOrganizations.rejected, (state, action) => {
            state.error.fetch = action.error.message || 'Error fetching organizations';
        })
            .addCase(updateUserAvatar.fulfilled, (state, action) => {
            state.data = Object.assign(Object.assign({}, state.data), { avatar: action.payload.avatar });
            state.status.update = 'succeeded';
        })
            .addCase(updateUserAvatar.rejected, (state, action) => {
            state.status.update = 'failed';
            state.error.fetch = action.error.message || 'Error fetching components';
        });
    },
});
export const { logout, updateUserAvatarStore } = userSlice.actions;
export const selectUserProfileDataData = (state) => state.user.data;
export const selectTennantData = (state) => state.user.tennant;
export const selectOrganizations = (state) => state.user.organizations;
export const selectOrganizationRole = (state) => state.user.organizationRole;
export const selectIsOrganizationOwner = (state) => state.user.organizationRole === 'OWNER';
export const selectUserProfileDataStatus = (state) => state.user.status.fetch;
export default userSlice.reducer;
